/* pages/BusinessSingle.jsx */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useParams, Link } from "react-router-dom";
import "./../style/businesssingle.css";

const FallbackHero =
  "https://snasso.quintbrothers.com/wp-content/uploads/2025/09/defulat-image-scaled.webp";
const FallbackLogo = "https://via.placeholder.com/120?text=Logo";

const BusinessSingle = () => {
  const { slugOrId } = useParams();
  const location = useLocation();
  const preloaded = location.state?.business || null;

  const [biz, setBiz] = useState(preloaded);
  const [loading, setLoading] = useState(!preloaded);

  useEffect(() => {
    const fetchIfNeeded = async () => {
      if (biz) return;
      setLoading(true);
      try {
        let data = null;

        // 1) Preferred: fetch by slug
        try {
          const { data: d } = await axios.get(
            `https://biz.dropyourbusiness.com/api/business/live/${encodeURIComponent(
              slugOrId
            )}`
          );
          data = d?.business || null;
        } catch (_) {
          /* no-op */
        }

        // 2) Fallback: numeric id
        if (!data && /^\d+$/.test(slugOrId)) {
          try {
            const { data: d2 } = await axios.get(
              `https://biz.dropyourbusiness.com/api/business/${slugOrId}`
            );
            data = d2?.business || null;
          } catch (_) {
            /* no-op */
          }
        }

        setBiz(data || null);
      } catch (e) {
        console.error("Failed to fetch business detail", e);
        setBiz(null);
      } finally {
        setLoading(false);
      }
    };

    fetchIfNeeded();
  }, [slugOrId, biz]);

  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader" />
        <p>Loading…</p>
      </div>
    );
  }

  if (!biz) {
    return (
      <div className="no-data">
        Business not found. <Link to="/categories">Go back</Link>
      </div>
    );
  }

  // --------- derived fields ----------
  const hero =
    (Array.isArray(biz.images) && biz.images[0]) || biz.logo || FallbackHero;

  const logoSrc = biz.logo?.trim() ? biz.logo : FallbackLogo;

  const addr = biz.address || {};
  const streetLine = [addr.street_address_1, addr.street_address_2]
    .filter(Boolean)
    .join(", ");
  const localityLine = [addr.city?.name, addr.state?.name, addr.country?.name]
    .filter(Boolean)
    .join(", ");
  const fullAddress = [streetLine, localityLine].filter(Boolean).join(", ");
  const mapHref = fullAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        fullAddress + (addr.pincode ? ` ${addr.pincode}` : "")
      )}`
    : null;

  const email = addr.email || "";
  const phone1 = addr.contact_1 || "";
  const phone2 = addr.contact_2 || "";
  const website = addr.url || "";

  const tel1 = phone1 ? `tel:${phone1.replace(/\s+/g, "")}` : null;
  const tel2 = phone2 ? `tel:${phone2.replace(/\s+/g, "")}` : null;
  const wa1 = phone1 ? `https://wa.me/${phone1.replace(/[^\d]/g, "")}` : null;

  const socials = biz.social_accounts || {};
  const otherImages = Array.isArray(biz.images) ? biz.images.slice(1) : [];

  // hours can be object or array; normalize to [{day, hours}]
  const whObj = biz.business_time?.working_hours;
  const is247 = !!biz.business_time?.is_24_7;
  const dayOrder = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];
  let workingHours = [];
  if (whObj && typeof whObj === "object") {
    workingHours = dayOrder
      .filter((d) => whObj[d])
      .map((d) => ({ day: d, hours: whObj[d] }));
  } else if (Array.isArray(whObj)) {
    workingHours = whObj.map((it, i) => ({
      day: it.day || `Day ${i + 1}`,
      hours: it.hours || it.time || "—",
    }));
  }

  const isExternalLive =
    biz?.live_url && /^https?:\/\//i.test(String(biz.live_url));

  return (
    <div className="bs">
      {/* HERO */}
      <div className="bs-hero" style={{ backgroundImage: `url("${hero}")` }}>
        <div className="bs-hero__overlay" />
        <div className="bs-hero__inner">
          <div className="bs-hero__text">
            <div className="bs-hero__top">
              <img
                src={logoSrc}
                alt={`${biz.business_name} logo`}
                className="bs-logo"
                onError={(e) => {
                  e.currentTarget.src = FallbackLogo;
                }}
              />
              <h1 className="bs-title">{biz.business_name}</h1>
            </div>

            <div className="bs-chips">
              <span className="bs-chip">
                {biz?.category?.name || "Uncategorized"}
              </span>
              {biz?.sub_category?.name && (
                <span className="bs-chip">{biz.sub_category.name}</span>
              )}
              {biz.owner_name && biz.owner_name !== "-" && (
                <span className="bs-chip">Owner: {biz.owner_name}</span>
              )}
            </div>

            {fullAddress && (
              <p className="bs-addr">
                {fullAddress}
                {addr.pincode ? ` - ${addr.pincode}` : ""}
              </p>
            )}

            <div className="bs-cta">
              {tel1 && (
                <a className="bs-btn" href={tel1}>
                  Call
                </a>
              )}
              {wa1 && (
                <a
                  className="bs-btn"
                  href={wa1}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  WhatsApp
                </a>
              )}
              {email && (
                <a className="bs-btn" href={`mailto:${email}`}>
                  Email
                </a>
              )}
              {website && (
                <a
                  className="bs-btn"
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit Website
                </a>
              )}
              {isExternalLive && (
                <a
                  className="bs-btn"
                  href={biz.live_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Business Site
                </a>
              )}
              {mapHref && (
                <a
                  className="bs-btn"
                  href={mapHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get Directions
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* BODY */}
      <div className="bs-body">
        {/* Main column */}
        <div className="bs-main">
          {/* About */}
          <section className="bs-card">
            <h2 className="bs-h2">About</h2>
            <p className="bs-p">{biz.description || "No description available."}</p>
          </section>

          {/* Gallery */}
          <section className="bs-card">
            <h2 className="bs-h2">Gallery</h2>
            {otherImages.length ? (
              <div className="bs-gallery">
                {otherImages.map((img, i) => (
                  <img
                    key={i}
                    src={img}
                    className="bs-gallery__img"
                    alt={`Gallery ${i + 1}`}
                    onError={(e) => {
                      e.currentTarget.src = FallbackHero;
                    }}
                  />
                ))}
              </div>
            ) : (
              <p className="bs-muted">No more images to show.</p>
            )}
          </section>
                    {/* Socials */}
          <section className="bs-card">
            <h2 className="bs-h2">Socials</h2>
            <ul className="bs-socials">
              {socials.facebook_url ? (
                <li>
                  <a
                    className="bs-a"
                    href={socials.facebook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Facebook
                  </a>
                </li>
              ) : null}
              {socials.instagram_url ? (
                <li>
                  <a
                    className="bs-a"
                    href={socials.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Instagram
                  </a>
                </li>
              ) : null}
              {socials.x_url ? (
                <li>
                  <a
                    className="bs-a"
                    href={socials.x_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    X (Twitter)
                  </a>
                </li>
              ) : null}
              {socials.linkedin_url ? (
                <li>
                  <a
                    className="bs-a"
                    href={socials.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    LinkedIn
                  </a>
                </li>
              ) : null}
              {socials.youtube_url ? (
                <li>
                  <a
                    className="bs-a"
                    href={socials.youtube_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    YouTube
                  </a>
                </li>
              ) : null}
              {socials.pinterest_url ? (
                <li>
                  <a
                    className="bs-a"
                    href={socials.pinterest_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Pinterest
                  </a>
                </li>
              ) : null}
              {!socials.facebook_url &&
                !socials.instagram_url &&
                !socials.x_url &&
                !socials.linkedin_url &&
                !socials.youtube_url &&
                !socials.pinterest_url && <li>Not available.</li>}
            </ul>
          </section>

          {/* Business Hours */}
          <section className="bs-card">
            <h2 className="bs-h2">Business Hours</h2>
            {is247 ? (
              <p className="bs-p">Open 24/7</p>
            ) : workingHours.length ? (
              <ul className="bs-hours">
                {workingHours.map((it, idx) => {
                  const label =
                    it.day.charAt(0).toUpperCase() + it.day.slice(1);
                  return (
                    <li key={idx} className="bs-hours__row">
                      <span>{label}</span>
                      <span>{it.hours}</span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="bs-p">Working hours not provided.</p>
            )}
          </section>
        </div>

        {/* Sidebar */}
        <aside className="bs-side">
          {/* Quick Info */}
          <section className="bs-card bs-sticky">
            <h2 className="bs-h2">Quick Info</h2>

            <div className="bs-kv">
              <span>Category</span>
              <p>{biz?.category?.name || "Uncategorized"}</p>
            </div>

            {biz?.sub_category?.name && (
              <div className="bs-kv">
                <span>Sub-category</span>
                <p>{biz.sub_category.name}</p>
              </div>
            )}

            {biz.owner_name && biz.owner_name !== "-" && (
              <div className="bs-kv">
                <span>Owner</span>
                <strong>{biz.owner_name}</strong>
              </div>
            )}

            {fullAddress && (
              <div className="bs-kv">
                <span>Address</span>
                <p>
                  {streetLine}
                  <br />
                  {localityLine}
                  {addr.pincode ? ` - ${addr.pincode}` : ""}
                </p>
              </div>
            )}

            {email && (
              <div className="bs-kv">
                <span>Email</span>
                <p>
                  <a className="bs-a" href={`mailto:${email}`}>
                    {email}
                  </a>
                </p>
              </div>
            )}

            {phone1 && (
              <div className="bs-kv">
                <span>Phone</span>
                <p>
                  <a className="bs-a" href={tel1}>
                    {phone1}
                  </a>
                </p>
              </div>
            )}

            {phone2 && (
              <div className="bs-kv">
                <span>Alt. Phone</span>
                <p>
                  <a className="bs-a" href={tel2}>
                    {phone2}
                  </a>
                </p>
              </div>
            )}

            {website && (
              <div className="bs-kv">
                <span>Website</span>
                <p>
                  <a
                    className="bs-a"
                    href={website}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {website}
                  </a>
                </p>
              </div>
            )}
          </section>


        </aside>
      </div>
    </div>
  );
};

export default BusinessSingle;
